import { Controller } from "@hotwired/stimulus";
import RequestHelper from "../helpers/request_helper";
import ProgressBar from "../helpers/progress_bar";

// Mounts on gives/giphy/show, gives/logos/index and gives/polaroids/{index,new}, all reached as
// Turbo frame swaps from the design, checkout and redemption pages. The giphy pagination links
// used to be bound to jquery-pjax here; they sit inside the frame now, so the frame drives them
// and no binding is needed.
//
// Every write below sends the same headers. PolaroidsController#update and LogosController#update
// branch on request.xhr?, so X-Requested-With is required to reach their JSON branch at all;
// GiphyController#update and DefaultsController#update/destroy build their JSON with
// render_to_string, where the full Accept is what keeps lookup_context.formats unconstrained.
const XHR_JSON_HEADERS = {
  "X-Requested-With": "XMLHttpRequest",
  "Accept": "application/json, text/plain, */*"
};

// Returns a jQuery promise rather than a native one: sendUpdatedDesign's callers below rely on
// `.always()`.
const preloadImages = ($element) => {
  const $images = $element.find("img");
  const promises = $images.map(function () { return $.Deferred(); }).get();

  $images.each(function (index) {
    const image = new Image();
    image.onload = function () { promises[index].resolve(); };
    image.onerror = function () { promises[index].reject(); };
    image.src = $(this).attr("src");
  });

  return $.when.apply($, promises);
};

export default class extends Controller {
  updateDesign(e) {
    e.preventDefault();

    const form = e.currentTarget.closest("form");
    const path = form.action;

    this.markDesignAsCurrent(form);

    this.sendUpdatedDesign({ path });
  }

  updateGiphyDesign(e) {
    e.preventDefault();

    const $form = $(e.currentTarget).parent("form");
    const path = $form.attr("action");
    const externalUrl = $form.find("#build_external_url").val();
    const dimensionWidth = $form.find("#build_dimension_width").val();
    const dimensionHeight = $form.find("#build_dimension_height").val();
    const dimensionString = $form.find("#build_dimension_string").val();

    this.sendUpdatedDesign({
      path,
      data: {
        external_url: externalUrl,
        dimension_string: dimensionString,
        dimension_width: dimensionWidth,
        dimension_height: dimensionHeight
      }
    });
  }

  giphySearch(e) {
    e.preventDefault();

    let $form;

    if ($(e.currentTarget).parents("form").length > 0) {
      $form = $(e.currentTarget).parents("form");
    } else {
      $form = $(e.currentTarget);
    }

    const query = $form.find("#giphy_search").val();

    if (query.trim().length === 0) { return; }

    const action = $form.attr("action");

    const options = RequestHelper.defaultRequestOptions();
    options.headers = Object.assign({}, options.headers, XHR_JSON_HEADERS);

    const url = `${action}?query=${encodeURIComponent(query)}`;

    fetch(url, options).then((response) => response.text()).then((html) => {
      $(this.element).replaceWith(html);
    });
  }

  // S2, not a surface: `tagHtml` below is a fixed literal with nothing interpolated into it
  // (pattern #41's sibling case -- a constant is not a sink), so there is no partial to name here.
  markDesignAsCurrent(form) {
    const tagHtml = `
      <div class="tag tag-redeemed" data-js="current-tag">
        <div class="tag-body">
          <i class="icon-lasso-fill-check"></i>
          Selected
        </div>
      </div>
    `;

    const existingTag = document.querySelector('[data-js="current-tag"]');
    if (existingTag) { existingTag.remove(); }
    form.insertAdjacentHTML("afterbegin", tagHtml);
  }

  removeAsDefault(event) {
    event.preventDefault();

    const { id, resourceName } = event.params;
    const action = event.currentTarget.pathname;

    this.sendDefaultAction({
      method: "DELETE",
      path: action,
      params: { id, resource_name: resourceName },
      resourceName
    });
  }

  setAsDefault(event) {
    event.preventDefault();

    const { id, resourceName } = event.params;
    const action = event.currentTarget.pathname;

    this.sendDefaultAction({
      method: "PUT",
      path: action,
      params: { id, resource_name: resourceName },
      resourceName
    });
  }

  // --- internal helpers below, not Stimulus actions ---

  sendUpdatedDesign(options) {
    ProgressBar.start();

    const path = options.path;
    const data = options.data || {};

    const fetchOptions = RequestHelper.defaultPatchOptions();
    fetchOptions.headers = Object.assign({}, fetchOptions.headers, XHR_JSON_HEADERS, { "Content-Type": "application/json" });
    fetchOptions.body = JSON.stringify(data);

    fetch(path, fetchOptions).then((response) => {
      if (!response.ok) { throw response; }
      return response.json();
    }).then((responseData) => {
      // S4, accepted: `responseData.html` is server-rendered HTML from our own origin, and this is
      // the single source for every `.html(newDom)` and `replaceWith(newDom)` sink in this method.
      // The four actions named in the header build it with render_to_string:
      // Gives::LogosController#update and Gives::GiphyController#update render
      // gives/designs/_card; Gives::PolaroidsController#update renders that same _card, or
      // gives/previews/recipients/design_ai on the AI checkout branch, or
      // gives/designs/_redemption_page when the design is not for the card.
      //
      // Read all three plus every partial they reach (the design_partial_type partials in
      // gives/designs/, gives/_email_preview_footer, gives/_email_preview_extra_links,
      // gives/designs/_card_ai and unties/designs/): no `raw`/`html_safe`/`!=` anywhere in that
      // tree, and best_logo_image emits only `image_tag`, whose alt/class Rails escapes. This
      // matters more here than at an innerHTML sink because jQuery's `.html()` executes an
      // embedded `<script>` where innerHTML would not (D4) -- the one `:javascript` block in the
      // tree, unties/designs/_card, interpolates nothing and is reached only by the
      // _redemption_page branch, which lands on replaceWith rather than on `.html()`.
      const newDom = $(responseData.html);
      const preload = preloadImages(newDom);
      const $modal = document.querySelector(".edit-redemption-image-modal");

      preload.then(() => {
        if (window.location.pathname.includes("/give/design")) {
          $("#current-card").html(newDom);

          // Long-standing: this has always fired unconditionally, whether or not the mobile
          // menu was actually open.
          this.closeMobileMenu();
        } else if (window.location.pathname.includes("/give/checkout")) {
          $(".review-content").html(newDom);
          $modal.classList.remove("show");
        } else {
          $("#current-redemption-page").replaceWith(newDom);
          const previewControls = document.querySelector(".redemption-page-header .preview");

          if (previewControls) {
            if (newDom[0].dataset.configurable === "true") {
              previewControls.classList.remove("hidden");
            } else {
              previewControls.classList.add("hidden");
            }
          }
          if ($modal) {
            $modal.classList.remove("show");
          }
        }
      }, (e) => {
        console.warn("error loading images", e);

        // Image-preload failure repaints the same `newDom` the success branch above does; the S4
        // provenance note where it is built covers these sinks too.
        if (window.location.pathname.includes("/give/checkout")) {
          $(".review-content").html(newDom);
          $modal.classList.remove("show");
        } else {
          $("#current-redemption-page").replaceWith(newDom);

          if (e === undefined && $modal) {
            $modal.classList.remove("show");
          }

          const previewControls = document.querySelector(".redemption-page-header .preview");

          if (previewControls) {
            if (newDom[0].dataset.configurable === "true") {
              previewControls.classList.remove("hidden");
            } else {
              previewControls.classList.add("hidden");
            }
          }
        }
      }).always(() => {
        $.smoothScroll();
        // Dispatched here, regardless of image-preload success/failure, so give_redemption_
        // controller.js's own listener (and give_redemption_mobile_modal_controller.js's)
        // re-initializes even when the image preload above rejected.
        document.dispatchEvent(new CustomEvent("redemptionPageUpdated"));
        ProgressBar.stop();
      });
    }, (response) => {
      // Known quirk: a failed PATCH only warns, and ProgressBar.stop() lives in the preload
      // success path, so the progress bar is left running.
      console.warn(response);
    });
  }

  sendDefaultAction(options) {
    ProgressBar.start();

    const { method, path, params, resourceName } = options;

    let url = path;
    let fetchOptions;

    if (method === "DELETE") {
      // $http.delete(path, { params }) sends params as a query string, not a body.
      fetchOptions = RequestHelper.defaultDeleteOptions();
      fetchOptions.headers = Object.assign({}, fetchOptions.headers, XHR_JSON_HEADERS);
      url = `${path}?${new URLSearchParams(params).toString()}`;
    } else {
      // $http.put(path, params) sends params as the JSON body.
      fetchOptions = RequestHelper.defaultPutOptions();
      fetchOptions.headers = Object.assign({}, fetchOptions.headers, XHR_JSON_HEADERS, { "Content-Type": "application/json" });
      fetchOptions.body = JSON.stringify(params);
    }

    fetch(url, fetchOptions).then((response) => {
      if (!response.ok) { throw response; }
      return response.json();
    }).then((data) => {
      // Server-rendered from our own origin, same as sendUpdatedDesign above but a different pair
      // of templates: Gives::DefaultsController#update/#destroy render_to_string either
      // gives/logos/index or gives/polaroids/new (template_path), layout: false. Neither uses
      // `raw`/`html_safe`/`!=`, and the only values they interpolate are logo/design names and
      // ids, HAML-escaped. `resource_name` cannot steer this anywhere else -- check_permissions
      // rejects anything outside ALLOWED_RESOURCES with a 403 before the action runs.
      const newDom = $(data.html);
      const targetSelector = resourceName === "Logo" ? ".design-tray.logos" : ".design-tray.polaroids";

      $(targetSelector).replaceWith(newDom);

      ProgressBar.stop();
    }, (response) => {
      console.warn("Default action failed:", response);
      ProgressBar.stop();
    });
  }

  closeMobileMenu() {
    const mobileMenu = document.querySelector(".mobile-menu");
    mobileMenu.classList.remove("open");
  }
}
